import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import { SeatIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { updatedAllocatedPlaces } from '../../../api/trip-dates/update-allocated-places'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import { countFilters } from '../../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../../components/elements/nav-bar'
import { getTripDateColumns } from '../../../components/pages/trip-dates/trip-date.utils'
import { TripDateBookingToCenterForm } from '../../../components/pages/trip-dates/trip-date-booking-to-center-form'
import { PaxMessageBadge } from '../../../components/pages/trip-dates/trip-dates.ui'
import { TripDatesFilter } from '../../../components/pages/trip-dates/trip-dates-filter'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { TripDateFilter, TripDateLight } from '../../../lib/types/models/trip-booking/trip-date'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformTripDatesFilterToUrl,
  transformUrlToTripDatesFilter
} from '../../../lib/utils/filters-mapping/trip-dates'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

export function TripDatesListPage() {
  const queryClient = useQueryClient()
  const tableRef = useRef<DynamicTableRef<TripDateLight>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)

  const {
    modalState,
    toggleModal,
    closeModel,
    selectedData: tripDateId
  } = useModalManager<'filters' | 'display-trip', number>(['filters', 'display-trip'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<TripDateFilter>({
      initialFilters: { includesAllocations: true },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformTripDatesFilterToUrl,
      transformUrlToFilter: transformUrlToTripDatesFilter
    })

  const columns: Column<TripDateLight>[] = [
    ...getTripDateColumns<TripDateLight>('LIGHT'),
    {
      accessorKey: 'id',
      header: 'Répartition à faire',
      cell: ({ row: { original: tripDate } }) => {
        return (
          <div className={'flex flex-col gap-2 justify-center items-center py-2'}>
            {(tripDate.numberOfStudentsPlacesToAllocate > 0 ||
              tripDate.numberOfStaffPlacesToAllocate > 0) && (
              <PaxMessageBadge
                suffix={'à répartir'}
                numberOfStaff={tripDate.numberOfStaffPlacesToAllocate}
                numberOfStudents={tripDate.numberOfStudentsPlacesToAllocate}
              />
            )}
          </div>
        )
      }
    },
    {
      accessorKey: '',
      header: 'Répartition',
      cell: ({ row }) => (
        <Button label={'Voir'} onClick={() => toggleModal('display-trip', row.original.id)} />
      )
    }
  ]

  const { mutateAsync } = useMutation({
    mutationFn: updatedAllocatedPlaces,
    onError: showAxiosError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip-date', tripDateId] })
      toast.success('Places réparties avec succès')
      tableRef.current?.refetch()
      closeModel('display-trip')
    }
  })

  return (
    <div>
      <UserNavBar title={'Places à répartir'} icon={SeatIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<TripDateLight, TripDateFilter>
        ref={tableRef}
        mode={'normal'}
        title={'Toutes les places'}
        endpoint={API.tripDates.getAll.url}
        columns={columns}
        version={'LIGHT'}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter, ['includesAllocations'])}
        onFiltersClick={() => toggleModal('filters')}
        onLoadingChange={setIsTableLoading}
        allowSearchParam
        permissions={{ delete: false }}
      />

      <FilterDrawer<TripDateFilter>
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <TripDatesFilter type={'light'} filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {tripDateId && (
        <Modal
          isOpen={modalState['display-trip']}
          onCancel={() => closeModel('display-trip')}
          title={'Répartition des places'}
          icon={SeatIcon}
          size={'large'}
          hideActions
        >
          <TripDateBookingToCenterForm
            tripDateId={tripDateId}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('display-trip'),
              onConfirm: ({ data }) => mutateAsync({ id: tripDateId, data })
            })}
          />
        </Modal>
      )}
    </div>
  )
}
